import React, { useState } from 'react';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Typography from '@mui/material/Typography';
import { toast } from 'react-toastify';
import { styled, useTheme } from '@mui/material/styles';
import api from '../services/api';

import { Lock, ShieldCheck, Key, CheckCircle } from 'lucide-react';
import Stack from '@mui/material/Stack';

const validationSchema = Yup.object({
  password: Yup.string()
    .min(6, 'La password deve essere di almeno 6 caratteri')
    .required('Obbligatorio'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password'), null], 'Le password devono coincidere')
    .required('Obbligatorio'),
});

const RootStyle = styled(Grid)(({ theme }) => ({
  minHeight: '100vh',
  '@supports (min-height: 100dvh)': {
    minHeight: '100dvh',
  },
  overflowX: 'hidden',
}));

const ImageSection = styled(Grid)(({ theme }) => ({
  backgroundImage: 'url(https://source.unsplash.com/photo-1563986768609-322da13575f3?auto=format&fit=crop&w=1920&q=80)', // Secure Lock/Digital
  backgroundRepeat: 'no-repeat',
  backgroundColor: theme.palette.mode === 'light' ? theme.palette.grey[50] : theme.palette.grey[900],
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  position: 'relative',
}));

const Overlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.88) 0%, rgba(30, 41, 59, 0.88) 100%)',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  padding: theme.spacing(10),
  color: theme.palette.common.white,
}));

const FeatureItem = ({ icon: Icon, text }) => (
  <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
    <Box
      sx={{
        width: 48,
        height: 48,
        borderRadius: '50%',
        bgcolor: 'rgba(255, 255, 255, 0.1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Icon size={24} color="#00AB55" />
    </Box>
    <Typography variant="h6" sx={{ fontWeight: 500 }}>
      {text}
    </Typography>
  </Stack>
);

const FormSection = styled(Grid)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'flex-start',
  padding: theme.spacing(2.5, 2, 4),
  [theme.breakpoints.up('md')]: {
    justifyContent: 'center',
    padding: theme.spacing(8, 4),
  },
}));

export default function ResetPassword() {
  const navigate = useNavigate();
  const { token } = useParams();
  const theme = useTheme();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const formik = useFormik({
    initialValues: {
      password: '',
      confirmPassword: '',
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
        await api.put(`/auth/reset-password/${token}`, { password: values.password });
        toast.success('Password aggiornata con successo');
        navigate('/login');
      } catch (error) {
        toast.error(error.response?.data?.message || 'Errore durante l\'aggiornamento della password');
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  return (
    <RootStyle container component="main">
      <CssBaseline />
      <ImageSection item md={7} sx={{ display: { xs: 'none', md: 'block' } }}>
        <Overlay>
          <Typography variant="h3" sx={{ fontWeight: 800, mb: 2, color: 'common.white' }}>
            Sicurezza Account
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 400, opacity: 0.8, mb: 8, maxWidth: 480 }}>
            Imposta una nuova password sicura per proteggere il tuo account.
          </Typography>

          <Box>
            <FeatureItem icon={Key} text="Requisiti di Complessità Elevati" />
            <FeatureItem icon={ShieldCheck} text="Verifica Identità" />
            <FeatureItem icon={Lock} text="Protezione Brute-Force" />
            <FeatureItem icon={CheckCircle} text="Conferma Immediata" />
          </Box>
        </Overlay>
      </ImageSection>
      <Grid item xs={12} sm={12} md={5} component={Paper} elevation={6} square sx={{ overflowY: 'auto' }}>
        <FormSection>
          <Box sx={{ mb: { xs: 1.5, md: 3 } }}>
            <img
              src="/logo.svg"
              alt="DB Logo"
              style={{
                width: 'clamp(96px, 24vw, 160px)',
                height: 'clamp(96px, 24vw, 160px)',
                filter: theme.palette.mode === 'dark' ? 'brightness(0) invert(1)' : 'none'
              }}
            />
          </Box>
          <Typography component="h1" variant="h5">
            Nuova Password
          </Typography>
          <Box component="form" noValidate onSubmit={formik.handleSubmit} sx={{ mt: 1, width: '100%', maxWidth: 400 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Nuova Password"
              type="password"
              id="password"
              autoComplete="new-password"
              value={formik.values.password}
              onChange={formik.handleChange}
              error={formik.touched.password && Boolean(formik.errors.password)}
              helperText={formik.touched.password && formik.errors.password}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="confirmPassword"
              label="Conferma Password"
              type="password"
              id="confirmPassword"
              autoComplete="new-password"
              value={formik.values.confirmPassword}
              onChange={formik.handleChange}
              error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
              helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Aggiornamento...' : 'Reimposta Password'}
            </Button>
          </Box>
        </FormSection>
      </Grid>
    </RootStyle>
  );
}
